import type { LabResult } from "@ferrivia/api-contract";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { useAnalyteCopy } from "@ferrivia/lab-ui";
import { Button, Text } from "@ferrivia/ui";
import { formatCollectedOn } from "./trend";
import s from "./LabHistoryList.module.css";

/* Every draw as measured: date, the lab the client named (partner labs stay
   unnamed on client surfaces), and each reading as label, value, unit. No
   colour, flag or difference between draws. LATEST marks the newest draw by
   position in the API's newest-first order, never by anything in its values. */
export function LabHistoryList({ results, onAddOlder }: { results: LabResult[]; onAddOlder: () => void }) {
  const { t } = useTranslation("client");
  const { tag } = useFormatters();
  const { labelFor } = useAnalyteCopy();

  if (results.length === 0) return null;

  return (
    <section className={s.history} aria-labelledby="lab-history-title">
      <Text as="h2" size="md" weight="bold" id="lab-history-title">
        {t("results.history.title", "Aiemmat tulokset")}
      </Text>
      <ol className={s.list}>
        {results.map((result, index) => (
          <li key={result.id} className={s.row}>
            <div className={s.head}>
              <span className={s.date}>{formatCollectedOn(result.collectedOn, tag)}</span>
              {result.sourceLab ? (
                <span className={s.lab}>{result.sourceLab}</span>
              ) : result.labCode === null ? (
                <span className={s.lab}>{t("results.selfReported", "Itse ilmoitettu")}</span>
              ) : null}
              {index === 0 && <span className={s.latest}>{t("results.history.latest", "Uusin")}</span>}
            </div>
            <ul className={s.analytes}>
              {result.analytes.map((analyte) => (
                <li key={analyte.analyteCode}>{`${labelFor(analyte.analyteCode)} ${analyte.value} ${analyte.unit}`}</li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
      <Button variant="secondary" onClick={onAddOlder}>
        {t("results.history.addOlder", "Onko sinulla vanhempia tuloksia? Lisää nekin")}
      </Button>
    </section>
  );
}
